import React, { useCallback, useEffect, useState } from 'react';
import { API_ENDPOINTS } from '../config';
import { getBearerAuthHeaders } from '../utils/authFetchHeaders';
import { useAuth } from '../contexts/AuthContext';
import usePersistentTab from '../hooks/usePersistentTab';
import {
  AdminSubmissionDetailSummary,
  CsvPreviewTable,
} from '../components/StrategySubmissionReviewUi';
import './AdminLiveStrategyWizard.css';

function authHeaders() {
  return { 'Content-Type': 'application/json', ...getBearerAuthHeaders() };
}

const STATUS_FILTERS = [
  { id: 'pending', label: 'Pending' },
  { id: '', label: 'All' },
  { id: 'approved', label: 'Approved' },
  { id: 'rejected', label: 'Rejected' },
];

function statusLabel(status) {
  const value = String(status || '').trim().toLowerCase();
  if (value === 'pending') return 'Pending';
  if (value === 'approved') return 'Approved';
  if (value === 'rejected') return 'Rejected';
  if (value === 'withdrawn') return 'Withdrawn';
  return status || '—';
}

function categoryLabel(category) {
  return category === 'futures' ? 'Futures' : 'Stocks';
}

export default function AdminStrategySubmissionReview() {
  const { isAdmin } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = usePersistentTab('submissionReview.status', 'pending', STATUS_FILTERS.map((f) => f.id));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detail, setDetail] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [acting, setActing] = useState(false);
  const [approveResult, setApproveResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = statusFilter ? { status: statusFilter } : {};
      const res = await fetch(API_ENDPOINTS.adminStrategySubmissions(params), { headers: authHeaders() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to load submissions');
      setSubmissions(data.submissions || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err.message);
      setSubmissions([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    setApproveResult(null);
    setReviewNotes('');
    setError(null);
    try {
      const res = await fetch(API_ENDPOINTS.adminStrategySubmission(id), { headers: authHeaders() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to load submission');
      setDetail(data.submission);
    } catch (err) {
      setError(err.message);
    }
  };

  const closeDetail = () => {
    setDetail(null);
    setApproveResult(null);
    setReviewNotes('');
  };

  const approve = async () => {
    if (!detail) return;
    setActing(true);
    setError(null);
    try {
      const res = await fetch(API_ENDPOINTS.adminStrategySubmissionApprove(detail.id), {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ reviewNotes: reviewNotes.trim() || null }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to approve');
      setApproveResult(data);
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setActing(false);
    }
  };

  const reject = async () => {
    if (!detail) return;
    if (!reviewNotes.trim()) {
      setError('Review notes are required when rejecting.');
      return;
    }
    setActing(true);
    setError(null);
    try {
      const res = await fetch(API_ENDPOINTS.adminStrategySubmissionReject(detail.id), {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ reviewNotes: reviewNotes.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to reject');
      closeDetail();
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setActing(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="admin-live-strategy-page">
        <div className="admin-live-strategy-content">
          <h1 className="admin-live-strategy-title">Strategy submissions</h1>
          <p className="admin-live-strategy-intro">Full Admin access is required to review submissions.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-live-strategy-page">
      <div className="admin-live-strategy-content">
        <h1 className="admin-live-strategy-title">Strategy submission review</h1>
        <p className="admin-live-strategy-intro">
          Approve or reject strategy requests from Brand Admins. Approval creates the strategy
          using the same pipeline as New live strategy.
        </p>

        <div className="admin-submission-review-toolbar">
          <div className="admin-submission-review-tabs" role="tablist" aria-label="Submission status">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.id || 'all'}
                type="button"
                role="tab"
                aria-selected={statusFilter === f.id}
                className={[
                  'admin-submission-review-tab',
                  statusFilter === f.id ? 'is-active' : '',
                ].filter(Boolean).join(' ')}
                onClick={() => setStatusFilter(f.id)}
              >
                {f.label}
              </button>
            ))}
          </div>
          <span className="admin-submission-review-total">
            {total}
            {' '}
            {total === 1 ? 'submission' : 'submissions'}
          </span>
        </div>

        {error ? <div className="admin-live-strategy-error">{error}</div> : null}
        {loading ? <p className="admin-submission-review-loading">Loading…</p> : null}

        {!loading && !submissions.length ? (
          <div className="admin-submission-review-empty">
            {statusFilter
              ? `No ${statusFilter} submissions.`
              : 'No submissions yet.'}
          </div>
        ) : null}

        {!loading && submissions.length ? (
          <div className="admin-submission-review-table-wrap">
            <table className="admin-submission-review-table">
              <thead>
                <tr>
                  <th>Strategy</th>
                  <th>Brand</th>
                  <th>Category</th>
                  <th>Submitter</th>
                  <th>Status</th>
                  <th>Submitted</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {submissions.map((row) => (
                  <tr key={row.id}>
                    <td className="admin-submission-review-td-name">{row.displayName}</td>
                    <td>{row.entitySlug}</td>
                    <td>{categoryLabel(row.category)}</td>
                    <td className="admin-submission-review-td-email">{row.submittedByEmail}</td>
                    <td>
                      <span className={`admin-submission-review-status is-${row.status}`}>
                        {statusLabel(row.status)}
                      </span>
                    </td>
                    <td className="admin-submission-review-td-date">
                      {row.createdAt ? new Date(row.createdAt).toLocaleDateString() : '—'}
                    </td>
                    <td className="admin-submission-review-td-action">
                      <button type="button" className="admin-live-strategy-btn admin-live-strategy-btn-secondary" onClick={() => openDetail(row.id)}>
                        Review
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {detail ? (
          <div className="admin-submission-detail-panel">
            {!approveResult ? (
              <>
                <AdminSubmissionDetailSummary
                  submission={detail}
                  statusText={statusLabel(detail.status)}
                />

                {detail.csvText ? (
                  <section className="admin-submission-review-card admin-submission-review-card--wide admin-submission-csv-section">
                    <h4>CSV preview</h4>
                    <CsvPreviewTable csvText={detail.csvText} filename={detail.csvFilename} />
                  </section>
                ) : null}

                <div className="admin-submission-detail-actions-wrap">
                  <label className="admin-live-strategy-field admin-submission-review-notes">
                    <span>
                      Review notes
                      {detail.status === 'pending' ? ' (required for reject)' : ''}
                    </span>
                    <textarea
                      rows={4}
                      value={reviewNotes}
                      onChange={(e) => setReviewNotes(e.target.value)}
                      placeholder="Optional for approve. Required if rejecting."
                    />
                  </label>

                  {detail.status === 'pending' ? (
                    <div className="admin-live-strategy-actions">
                      <button type="button" className="admin-live-strategy-btn admin-live-strategy-btn-secondary" onClick={closeDetail}>Close</button>
                      <button type="button" className="admin-live-strategy-btn admin-live-strategy-btn-secondary" disabled={acting} onClick={reject}>Reject</button>
                      <button type="button" className="admin-live-strategy-btn admin-live-strategy-btn-primary" disabled={acting} onClick={approve}>
                        {acting ? 'Working…' : 'Approve & create strategy'}
                      </button>
                    </div>
                  ) : (
                    <div className="admin-live-strategy-actions">
                      <button type="button" className="admin-live-strategy-btn admin-live-strategy-btn-secondary" onClick={closeDetail}>Close</button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="admin-submission-review admin-submission-review--detail">
                <div className="admin-submission-review-hero">
                  <div className="admin-submission-review-hero-main">
                    <p className="admin-submission-review-eyebrow">Approved</p>
                    <h3 className="admin-submission-review-title">{approveResult.scenario?.displayName}</h3>
                    <p className="admin-submission-review-subtitle">
                      Created scenario slug:
                      {' '}
                      <span className="admin-submission-review-mono">{approveResult.scenario?.slug}</span>
                    </p>
                  </div>
                  <span className="admin-submission-review-status is-approved">Approved</span>
                </div>
                {approveResult.webhookUrl ? (
                  <p className="admin-submission-review-footnote">
                    Webhook URL:
                    {' '}
                    <code className="admin-submission-review-mono">{approveResult.webhookUrl}</code>
                  </p>
                ) : null}
                <div className="admin-live-strategy-actions">
                  <button type="button" className="admin-live-strategy-btn admin-live-strategy-btn-primary" onClick={closeDetail}>Done</button>
                </div>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
